import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

import type { PieChartData } from '@/utils/saasDashboardHelpers';

interface OpportunityPieChartProps {
  data: PieChartData[];
  title: string;
}

const OpportunityPieChart = ({ data, title }: OpportunityPieChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[400px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Custom tooltip content
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const total = item?.payload?.total || 0;
    const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
    return (
      <div className='bg-white p-3 border rounded shadow-lg'>
        <p className='font-medium' style={{ color: item.payload.color }}>
          {item.name}
        </p>
        <p className='text-sm text-muted-foreground'>
          {item.value.toLocaleString()} ({percent}%)
        </p>
      </div>
    );
  };

  // Calculate total for percentage display
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = data.map((item) => ({ ...item, total }));

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col md:flex-row gap-4'>
          {/* Pie Chart */}
          <div className='flex-shrink-0'>
            <ResponsiveContainer width={220} height={220}>
              <PieChart>
                <Pie
                  data={dataWithTotal}
                  cx='50%'
                  cy='50%'
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Custom Legend */}
          <ScrollArea className='flex-1 h-[220px]'>
            <div className='space-y-1 pr-4'>
              {data.map((item, index) => {
                const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
                return (
                  <div key={index} className='flex items-center gap-2 text-sm'>
                    <div
                      className='w-3 h-3 rounded-sm flex-shrink-0'
                      style={{ backgroundColor: item.color }}
                    />
                    <span className='truncate flex-1' title={item.name}>
                      {item.name}
                    </span>
                    <span className='text-muted-foreground text-xs whitespace-nowrap'>
                      {item.value.toLocaleString()} ({percent}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};

export default OpportunityPieChart;
